import { type Dispatch, useId } from 'react';
import { Typography } from '../typography';
import type { DropdownMenuGroupStyle } from './dropdown-menu';
import { type DropdownMenuItem, DropdownMenuListItem } from './list-item/dropdown-menu-list-item';
import styles from './dropdown-menu.module.scss';

interface MultiGroupDropdownMenuProps {
  ellipsizeOverflow?: boolean;
  groupedItems: Record<string, DropdownMenuItem[]>;
  groupStyle?: DropdownMenuGroupStyle;
  handleCloseMenu: VoidFunction;
  handleDialogClose: (subItemId: string) => void;
  handleDialogOpen: (subItemId: string) => void;
  isOpen: boolean;
  menuId: string;
  openDialogs: Set<string>;
  setObservedItemRef: Dispatch<React.SetStateAction<HTMLElement | null>>;
}

export function MultiGroupDropdownMenu({
  ellipsizeOverflow,
  groupedItems,
  groupStyle,
  handleCloseMenu,
  handleDialogClose,
  handleDialogOpen,
  isOpen,
  menuId,
  openDialogs,
  setObservedItemRef,
}: MultiGroupDropdownMenuProps) {
  const id = useId();
  const groupEntries = Object.entries(groupedItems);

  return (
    <div className={styles.menu} hidden={!isOpen || Boolean(openDialogs.size)} id={menuId}>
      {groupEntries.map(([group, itemsForGroup], i, groupArr) => {
        const groupLabelId = `${id}-group-${group.replace(/\s+/g, '-').toLowerCase()}`;

        return (
          <ul
            aria-label={groupStyle === 'heading' ? undefined : group}
            aria-labelledby={groupStyle === 'heading' ? groupLabelId : undefined}
            className={styles.group}
            key={group}
          >
            {groupStyle === 'heading' && group ? (
              <li className={styles.heading} id={groupLabelId}>
                <Typography lineHeight="1.5rem" size="0.75rem" transform="uppercase" weight="bold">
                  {group}
                </Typography>
              </li>
            ) : null}

            {itemsForGroup.map((item, j, arr) => (
              <DropdownMenuListItem
                ellipsizeOverflow={ellipsizeOverflow}
                handleCloseDialog={handleDialogClose}
                handleCloseMenu={handleCloseMenu}
                handleOpenDialog={handleDialogOpen}
                hasOpenDialog={openDialogs.has(item.id)}
                key={item.id}
                ref={i === groupArr.length - 1 && j === arr.length - 1 ? setObservedItemRef : undefined}
                {...item}
              />
            ))}
          </ul>
        );
      })}
    </div>
  );
}
